import React, { useState } from 'react';
import { FileText, Download, History, X } from 'lucide-react';
import ChatInterface from './components/ChatInterface';
import ProgressIndicator from './components/ProgressIndicator';
import ReportViewer from './components/ReportViewer';
import HistoryPanel from './components/HistoryPanel';
import { useReportGeneration } from './hooks/useReportGeneration';

type ViewType = 'chat' | 'progress' | 'report' | 'history' | 'streaming';

function App() {
    const [currentView, setCurrentView] = useState<ViewType>('chat');
    const [currentTaskId, setCurrentTaskId] = useState<string>('');

    const {
        isGenerating,
        streamContent,
        progress,
        reportContent,
        error,
        currentReports,
        generateReport,
        cancelGeneration,
        downloadReport,
        loadReportContent,
        reset
    } = useReportGeneration();

    const handleGenerateReport = async (query: string) => {
        console.log('前端: 开始生成报告，查询:', query);
        setCurrentView('streaming');

        const taskId = await generateReport(query);
        console.log('前端: 生成完成，taskId:', taskId);

        if (taskId) {
            setCurrentTaskId(taskId);
            setCurrentView('report');
            console.log('前端: 切换到报告视图');
        } else {
            console.log('前端: 生成失败，回到聊天视图');
            setCurrentView('chat');
        }
    };

    const handleCancelGeneration = () => {
        cancelGeneration();
        setCurrentView('chat');
        reset();
    };

    const handleNewReport = () => {
        setCurrentView('chat');
        setCurrentTaskId('');
        reset();
    };

    const handleViewHistory = () => {
        setCurrentView('history');
        setCurrentTaskId('');
    };

    const handleViewReportFromHistory = async (taskId: string) => {
        console.log('从历史记录查看报告，taskId:', taskId);
        setCurrentView('report');

        const success = await loadReportContent(taskId);
        if (success) {
            setCurrentTaskId(taskId);
            console.log('历史报告加载成功');
        } else {
            console.log('历史报告加载失败');
            setCurrentTaskId(taskId);
        }
    };

    // 用于 ReportViewer 的下载函数
    const handleDownloadFromViewer = (
        reportType?: 'no_reference' | 'with_reference',
        format?: 'md' | 'pdf'
    ) => {
        if (currentTaskId) {
            downloadReport(currentTaskId, reportType || 'no_reference', format || 'md');
        }
    };

    // 用于 HistoryPanel 的下载函数
    const handleDownloadFromHistory = (
        taskId: string,
        reportType?: 'no_reference' | 'with_reference'
    ) => {
        downloadReport(taskId, reportType || 'no_reference', 'md');
    };

    // 用于头部快捷按钮的下载函数
    const handleQuickDownload = () => {
        if (currentTaskId) {
            downloadReport(currentTaskId, 'no_reference', 'md');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* 头部 */}
            <header className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div className="flex items-center">
                            <FileText className="h-8 w-8 text-primary-600 mr-3" />
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    财务分析报告生成Agent
                                </h1>
                                <p className="text-sm text-gray-600">
                                    基于AI的财务分析报告自动生成平台
                                </p>
                            </div>
                        </div>
                        <div className="flex space-x-3">
                            <button
                                onClick={handleViewHistory}
                                className="btn-secondary flex items-center"
                                disabled={isGenerating}
                            >
                                <History className="h-4 w-4 mr-2" />
                                历史记录
                            </button>
                            {currentView === 'report' && (
                                <>
                                    <button
                                        onClick={handleQuickDownload}
                                        className="btn-primary flex items-center"
                                        disabled={!currentTaskId}
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        下载报告
                                    </button>
                                    <button
                                        onClick={handleNewReport}
                                        className="btn-secondary"
                                    >
                                        新建报告
                                    </button>
                                </>
                            )}
                            {currentView === 'streaming' && isGenerating && (
                                <button
                                    onClick={handleCancelGeneration}
                                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
                                >
                                    <X className="h-4 w-4 mr-2" />
                                    取消
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* 主内容区 */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {currentView === 'chat' && (
                    <ChatInterface onGenerateReport={handleGenerateReport} />
                )}

                {currentView === 'streaming' && (
                    <div className="max-w-4xl mx-auto">
                        {/* 流式进度显示 */}
                        <div className="card mb-6">
                            {progress && (
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center">
                                        <div className="animate-spin h-5 w-5 border-2 border-primary-600 border-t-transparent rounded-full mr-3" />
                                        <span className="font-medium text-gray-900">{progress.message}</span>
                                    </div>
                                    {progress.percent !== undefined && (
                                        <span className="text-sm text-gray-600">{progress.percent}%</span>
                                    )}
                                </div>
                            )}
                            {error && (
                                <div className="bg-red-50 text-red-800 p-4 rounded-lg mb-4">
                                    {error}
                                </div>
                            )}
                        </div>

                        {/* 流式内容显示 */}
                        {(streamContent || isGenerating) && (
                            <div className="card">
                                <div className="prose prose-lg max-w-none">
                                    <div className="markdown-content whitespace-pre-wrap">
                                        {streamContent || '正在生成报告...'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {currentView === 'progress' && (
                    <ProgressIndicator
                        progress={progress}
                        isGenerating={isGenerating}
                        error={error}
                    />
                )}

                {currentView === 'report' && (
                    <ReportViewer
                        content={reportContent}
                        taskId={currentTaskId}
                        reports={currentReports}
                        onNewReport={handleNewReport}
                        onDownload={handleDownloadFromViewer}
                    />
                )}

                {currentView === 'history' && (
                    <HistoryPanel
                        onViewReport={handleViewReportFromHistory}
                        onDownloadReport={handleDownloadFromHistory}
                    />
                )}
            </main>
        </div>
    );
}

export default App;
